import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  getGetOrgLogoQueryKey,
  getGetOrgLogoQueryOptions,
  useUploadOrgLogo,
  useDeleteOrgLogo,
  getGetCandidateExperienceDefaultsQueryKey,
  getGetCandidateExperienceDefaultsQueryOptions,
  useUpdateCandidateExperienceDefaults,
} from '@sara/api-client';
import { toast } from '@sara/ui/components/sonner';
import { toSuspense } from '../../../../lib/query';
import { BrandingLogoCard } from '../../components/BrandingLogoCard';

interface BrandingLogoContainerProps {
  orgId: string;
}

export function BrandingLogoContainerContent({ orgId }: BrandingLogoContainerProps) {
  const queryClient = useQueryClient();
  const { data: logo } = useSuspenseQuery(toSuspense(getGetOrgLogoQueryOptions({ orgId })));
  const { data: defaults } = useSuspenseQuery(
    toSuspense(getGetCandidateExperienceDefaultsQueryOptions({ orgId }))
  );

  const invalidateLogo = () =>
    queryClient.invalidateQueries({ queryKey: getGetOrgLogoQueryKey({ orgId }) });

  const { mutate: upload, isPending: isUploading } = useUploadOrgLogo({
    mutation: {
      onSuccess: () => {
        void invalidateLogo();
        toast.success('Logo updated');
      },
      onError: (error) =>
        toast.error('Failed to upload logo', {
          description: error instanceof Error ? error.message : undefined,
        }),
    },
  });

  const { mutate: remove, isPending: isRemoving } = useDeleteOrgLogo({
    mutation: {
      onSuccess: () => {
        void invalidateLogo();
        toast.success('Logo removed');
      },
      onError: (error) =>
        toast.error('Failed to remove logo', {
          description: error instanceof Error ? error.message : undefined,
        }),
    },
  });

  const { mutate: updateDefaults, isPending: isUpdatingDefaults } =
    useUpdateCandidateExperienceDefaults({
      mutation: {
        onSettled: () =>
          queryClient.invalidateQueries({
            queryKey: getGetCandidateExperienceDefaultsQueryKey({ orgId }),
          }),
        onSuccess: () => toast.success('Defaults updated'),
        onError: (error) =>
          toast.error('Failed to update defaults', {
            description: error instanceof Error ? error.message : undefined,
          }),
      },
    });

  return (
    <BrandingLogoCard
      logoUrl={logo.logoUrl}
      logoPublicId={logo.logoPublicId ?? null}
      onUpload={(file) => upload({ data: { file }, params: { orgId } })}
      onRemove={() => remove({ params: { orgId } })}
      isLogoPending={isUploading || isRemoving}
      showOrgLogo={defaults.showOrgLogo}
      // Partial PATCH — the toggle sends only its own field.
      onToggleShowOrgLogo={(showOrgLogo) =>
        updateDefaults({ data: { showOrgLogo }, params: { orgId } })
      }
      isDefaultsPending={isUpdatingDefaults}
    />
  );
}
